import React, { useState, useEffect } from 'react';

const ViewAnswerModal = ({
  onClose,
  question,
  detail,
  answer,
  questionId,
  onAnswerPosted,
  editable = true,
  initialTags = []
}) => {
  const [visible, setVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedAnswer, setEditedAnswer] = useState(answer);
  const [currentAnswer, setCurrentAnswer] = useState(answer);
  const [tags, setTags] = useState(initialTags);
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState(null);

  // Sync when `answer` or `initialTags` change
  useEffect(() => {
    setEditedAnswer(answer);
    setCurrentAnswer(answer);
  }, [answer]);

  useEffect(() => {
    setTags((initialTags || []).map(t => t.toLowerCase()));
  }, [initialTags]);

  // Fade-in
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const removeTag = idx => {
    setTags(tags.filter((_, i) => i !== idx));
  };

  const handleTagKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const t = tagInput.trim().toLowerCase();
      if (t && !tags.includes(t)) {
        setTags([...tags, t]);
      }
      setTagInput('');
    }
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 600);
  };

  const handleOverlayClick = e => {
    if (e.target.classList.contains('modal')) {
      handleClose();
    }
  };

  const handleEditSubmit = async e => {
    e.preventDefault();
    if (!editedAnswer.trim()) {
      setError('Answer is required');
      return;
    }
    if (!questionId) {
      setError('Invalid question ID');
      return;
    }

    try {
      const res = await fetch(`/api/questions/${questionId}/answer`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answer: editedAnswer, tags, skipNotify: true })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Failed to update answer');
      } else {
        // 1) update local state
        setCurrentAnswer(editedAnswer);
        setTags(data.tags || []);                // ← grab the saved tags
        setIsEditing(false);
        setError(null);

        // 2) notify parent so it can update its list
        if (onAnswerPosted) onAnswerPosted(data);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to update answer. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditedAnswer(currentAnswer);
    setTags((initialTags || []).map(t => t.toLowerCase()));
    setError(null);
    setIsEditing(false);
  };

  return (
    <div
      className={`modal view-answer-modal ${visible ? 'show' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className="card modal-card" onClick={e => e.stopPropagation()}>
        <span className="close-modal" onClick={handleClose}>
          <img src="/images/i_modal_close.svg" alt="Close" />
        </span>

        {!isEditing ? (
          <div className="view-answer-content center">
            <div className="badge-question small dark">
              <div className="badge-question-number small">Q</div>
            </div>
            <h5 className="margin-top-m">{question}</h5>
            {detail && <p>{detail}</p>}
            <div className="divider dotted" />

            <div className="badge-question small dark">
              <div className="badge-question-number small">A</div>
            </div>
            <p>{currentAnswer}</p>
            <div className="modal-actions">
              {editable && (
                <button
                  className="text-link"
                  onClick={() => setIsEditing(true)}
                  type="button"
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="edit-answer-content">
            <div className="text-center">
              <div className="badge-question-large">
                <img src="/images/badge_Q_S_B.svg" alt="Question Badge" />
              </div>
              <h5 className="margin-top-m">{question}</h5>
              {detail && <p>{detail}</p>}
            </div>
            <div className="divider dotted" />

            <form onSubmit={handleEditSubmit}>
              <div className="text-uppercase margin-top-l">Your answer</div>
              <textarea
                name="answer"
                value={editedAnswer}
                onChange={e => setEditedAnswer(e.target.value)}
                required
              />

              {/* TAGS INPUT UI */}
              <div className="text-uppercase margin-top-l">Tags</div>
              <div className="tags-input">
                {tags.map((t, i) => (
                  <span key={i} className="tag">
                    {t}
                    <button
                      type="button"
                      className="remove-tag"
                      onClick={() => removeTag(i)}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  className="tag-input"
                  type="text"
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                />
              </div>

              {error && <p className="fail">{error}</p>}

              <div className="modal-footer">
                <button
                  className="grey"
                  type="button"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
                <button type="submit">Post Answer</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewAnswerModal;
